import clsx from 'clsx';
import { format } from 'date-fns';
import { parseISO } from 'date-fns/parseISO';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type DateRange } from 'react-day-picker';
import { useIntl } from 'react-intl';

import { ClearFiltersButton } from '@/components/base/clear-filters-button';
import { Search } from '@/components/base/search';
import {
  DateRangePicker,
  DateRangePreset,
  LIFETIME,
} from '@/components/ui/date-range-picker';
import { Select } from '@/components/ui/select';
import { useTableContext } from '@/contexts/table-context';
import { useLocalDateRanges } from '@/hooks/use-local-date-ranges';
import { usePostHog } from '@/hooks/use-posthog';

import { isDateRangeApplied } from './campaigns-filter-apply';
import { useCampaignFilters } from './use-campaign-filters';

const API_DATE_FORMAT = 'yyyy-MM-dd';

export function Filters() {
  const intl = useIntl();
  const posthog = usePostHog();
  const localDays = useLocalDateRanges();
  const {
    searchQuery,
    setSearchQuery,
    filters: appliedFilters,
    setFilters,
    dateRange,
    setDateRange,
    sorting,
    resetQueryState,
  } = useTableContext();
  const filters = useCampaignFilters();
  // Remount Search when the bar is cleared so its local input state resets —
  // Search only seeds from `initialValue` on mount.
  const [searchResetKey, setSearchResetKey] = useState(0);

  // A search "session" runs from the first keystroke until a 30s idle gap or
  // blur — `search_start` fires once per session; `search_complete` (fired
  // from CampaignsTable once results for the settled query resolve) closes it.
  const searchSessionActiveRef = useRef(false);
  const searchIdleTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const endSearchSession = useCallback(() => {
    searchSessionActiveRef.current = false;
    clearTimeout(searchIdleTimerRef.current);
  }, []);

  const handleSearchKeystroke = useCallback(() => {
    if (!searchSessionActiveRef.current) {
      searchSessionActiveRef.current = true;
      posthog.capture('source_campaigns:search_start');
    }
    clearTimeout(searchIdleTimerRef.current);
    searchIdleTimerRef.current = setTimeout(endSearchSession, 30_000);
  }, [posthog, endSearchSession]);

  const handleSearchQueryChange: Dispatch<SetStateAction<string | undefined>> =
    useCallback(
      updater => {
        setSearchQuery(updater);
      },
      [setSearchQuery],
    );

  // `source_campaigns:filter_apply` fires from CampaignsTable once the
  // filtered results load, so its result_count is the filtered total.
  const handleFilterApply = useCallback(
    (selectedValues: (string | boolean | undefined)[], key: string) => {
      setFilters(prev => ({
        ...prev,
        [key]: selectedValues,
      }));
    },
    [setFilters],
  );

  const selectedDate = useMemo<DateRange>(
    () => ({
      from: dateRange.startDate ? parseISO(dateRange.startDate) : undefined,
      to: dateRange.endDate ? parseISO(dateRange.endDate) : undefined,
    }),
    [dateRange],
  );

  // No explicit preset stored (the untouched default) reads the same as a
  // completely open-ended range, so it displays/behaves as Lifetime.
  const selectedPreset: DateRangePreset =
    dateRange.preset ?? (dateRange.startDate ? 'custom' : LIFETIME);

  const handleDateRangeApply = useCallback(
    ({ preset, range }: { preset?: DateRangePreset; range: DateRange }) => {
      setDateRange({
        preset:
          preset && preset !== 'custom' && preset !== LIFETIME
            ? preset
            : undefined,
        startDate: range.from ? format(range.from, API_DATE_FORMAT) : undefined,
        endDate: range.to ? format(range.to, API_DATE_FORMAT) : undefined,
      });
    },
    [setDateRange],
  );

  const activeFilterCount = useMemo(() => {
    const multiSelectCount =
      appliedFilters.channelIds.length +
      appliedFilters.statuses.length +
      appliedFilters.positionIds.length +
      appliedFilters.locationIds.length +
      appliedFilters.brandIds.length;
    const searchCount = searchQuery?.trim() ? 1 : 0;
    const sortCount = sorting.length > 0 ? 1 : 0;
    const dateCount = isDateRangeApplied(dateRange) ? 1 : 0;

    return multiSelectCount + searchCount + sortCount + dateCount;
  }, [appliedFilters, searchQuery, sorting, dateRange]);

  const handleClearAll = useCallback(() => {
    posthog.capture('source_campaigns:filter_remove', {
      count: activeFilterCount,
    });
    // One atomic reset — calling setFilters + setSearchQuery + setSorting +
    // setDateRange in the same handler races `setSearchParams` and leaves
    // stale URL params. Only clear filters this page surfaces — not
    // openings-only keys (`isSelectedForSourcing`, `active`).
    resetQueryState({
      filters: {
        ...appliedFilters,
        channelIds: [],
        statuses: [],
        positionIds: [],
        locationIds: [],
        brandIds: [],
      },
      searchQuery: undefined,
      sorting: [],
      dateRange: {},
    });
    setSearchResetKey(key => key + 1);
  }, [posthog, activeFilterCount, resetQueryState, appliedFilters]);

  const dateRangeApplied = isDateRangeApplied(dateRange);

  return (
    <div className="flex flex-row flex-wrap items-center gap-2">
      <Search
        key={searchResetKey}
        initialValue={searchQuery}
        setSearchQuery={handleSearchQueryChange}
        onKeystroke={handleSearchKeystroke}
        onBlur={endSearchSession}
        placeholder={intl.formatMessage({
          defaultMessage: 'Search by opening name...',
          description: 'Campaigns filter bar search field placeholder',
        })}
        className="w-[245px]"
      />
      <DateRangePicker
        selectedDate={selectedDate}
        setSelectedDate={() => void 0}
        selectedPreset={selectedPreset}
        onPresetChange={() => void 0}
        onApply={handleDateRangeApply}
        localDays={localDays}
        className={clsx(
          'rounded-lg border',
          dateRangeApplied
            ? 'border-fx-brand-600 bg-fx-brand-50 hover:border-fx-brand-600 hover:bg-fx-brand-100'
            : 'border-fx-neutral-300 hover:bg-fx-brand-100 bg-white',
        )}
      />
      {filters.map(filter => (
        <Select
          key={filter.filterKey}
          id={filter.filterKey}
          options={filter.options ?? []}
          selectedValues={filter.selectedValues}
          prefixText={`${filter.label}:`}
          query={filter.query}
          setQuery={filter.setQuery}
          isLoading={filter.isFetching}
          onApply={handleFilterApply}
          className="rounded-lg"
        />
      ))}
      <ClearFiltersButton count={activeFilterCount} onClick={handleClearAll} />
    </div>
  );
}
